import { UseQueryResult } from '@tanstack/react-query';

import React, { useEffect, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';

import { ContextMetadataModel } from '../../../../api/api';
import useApiContexts from '../../Hooks/useApiContexts';
import ListWithFilter from '../ListWithFilter';

type GenericContextItemInputDialogProps<T> = {
	open: boolean;
	setOpen: (open: boolean) => void;
	itemsOfContext: (contextId?: string | undefined) => UseQueryResult<T[], Error>;
	item: T | undefined;
	onItemChange: (item: T | undefined, context?: ContextMetadataModel | undefined) => void;
	what: string;
	descriptionProperty?: string;
	icon?: (item: T) => React.ReactNode;
	initialContextId?: string;
};

export default function GenericContextInputItemDialog<T extends { id: string; name: string; contextId?: string }>({
	open,
	setOpen,
	item,
	onItemChange,
	itemsOfContext,
	what,
	descriptionProperty,
	icon,
	initialContextId,
}: GenericContextItemInputDialogProps<T>) {
	const { data: contexts } = useApiContexts();

	const [selectedContext, setSelectedContext] = useState<ContextMetadataModel | undefined>();

	const { data: items } = itemsOfContext(selectedContext?.id);
	const [selectedItem, setSelectedItem] = useState<T | undefined>();

	useEffect(() => {
		if (item && open) {
			setSelectedItem(item);
		}
	}, [item, open]);

	useEffect(() => {
		if (selectedItem && !selectedContext && contexts) {
			setSelectedContext(contexts.find((c) => c.id === selectedItem.contextId));
		} else if (!selectedItem && initialContextId && !selectedContext && contexts) {
			setSelectedContext(contexts.find((c) => c.id === initialContextId));
		}
	}, [contexts, initialContextId, selectedContext, selectedItem]);

	useEffect(() => {
		// Select first context as fallback only when no specific context is expected
		if (!selectedContext && contexts && contexts.length > 0 && !initialContextId) {
			setSelectedContext(contexts[0]);
		}
	}, [contexts, initialContextId, selectedContext]);

	const handleClose = () => {
		setOpen(false);
	};
	const handleSave = () => {
		onItemChange?.(selectedItem, selectedContext);
		setOpen(false);
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
			<DialogTitle>Select {what}</DialogTitle>
			<DialogContent dividers>
				<Stack direction="column" spacing={2} alignItems="center" height={'70vh'}>
					<FormControl fullWidth>
						<InputLabel>Context</InputLabel>
						<Select value={selectedContext?.id ?? ''} onChange={(event) => setSelectedContext(contexts?.find((c) => c.id === event.target.value))} label="Name">
							{contexts?.map((context) => (
								<MenuItem key={context.id} value={context.id}>
									{context.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<ListWithFilter
						items={items ?? []}
						selectedItemId={selectedItem?.id}
						onSelected={setSelectedItem}
						getKey={(item) => item.id}
						getDisplayName={(item) => item.name}
						getDescription={descriptionProperty ? (item) => (item as T & Record<string, string | undefined>)[descriptionProperty] : undefined}
						placeholder={`Search ${what}`}
						icon={icon}
						sx={{ width: '100%', overflowY: 'auto', flexGrow: 1 }}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button onClick={handleSave}>Save</Button>
			</DialogActions>
		</Dialog>
	);
}
